import React, { useState } from 'react'
import { Button, Input, Icon, IconButton } from '@shopflo/ui'
import { CurrencyInr, X } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { usePrerequisiteProducts } from '../../../hooks/usePrerequisiteProducts'
import {
  PurchaseConditionType,
  PurchaseConditionAmountType,
  CouponType,
  StandardPrerequisite,
  PrerequisiteType,
  DiscountFieldPaths
} from 'src/lib/types/discountsV2'
import FormErrorText from 'src/components/common/FormErrorText'
import CatalogueGroupControl from '../common/CatalogueGroupControl'
import { discountCatalogueErrors } from 'src/lib/utils/catalogueErrorHelpers'

const PurchaseConditionsProductList = () => {
  const {
    state: { prerequisite: rawPrerequisite, errors },
    actions: { setFormState }
  } = useDiscountForm()

  const prerequisite = rawPrerequisite as StandardPrerequisite
  const activeConfig = prerequisite
  const catalogueType =
    activeConfig?.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      ? 'COLLECTION'
      : 'PRODUCT_AND_VARIANT'

  const { prerequisiteCatalogue, metaData } = prerequisite
  const items = metaData?.prerequisiteCatalogue || []

  const {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveItemFromGroup,
    handleRemoveExcludedItem,
    handleVariantUpdate,
    handleAddEmptyGroup,
    handleValueChange
  } = usePrerequisiteProducts({
    prerequisite,
    setFormState,
    catalogueType
  })

  const [inputVisibility, setInputVisibility] = useState<Record<number, boolean>>({})

  const renderList = () => {
    const isAmountMode = activeConfig?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT

    const getInputValue = (groupIndex: number) => {
      const item = prerequisiteCatalogue[groupIndex]
      if (!item) return ''
      return (item.type === PrerequisiteType.PRICE ? item.price : item.quantity) || ''
    }

    const renderMaxValueInput = (groupIndex: number) => {
      const item = prerequisiteCatalogue[groupIndex]
      if (!item) return null

      const isPrice = item.type === PrerequisiteType.PRICE
      const maxValue = isPrice ? item.maxPrice : item.maxQuantity
      const errorPath = `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.${
        isPrice ? 'maxPrice' : 'maxQuantity'
      }`

      const isVisible = inputVisibility[groupIndex] ?? (maxValue !== undefined && maxValue !== null)

      if (isVisible) {
        return (
          <div className='flex items-center gap-2 bg-zinc-50 rounded relative group'>
            <span className='text-xs text-zinc-500 whitespace-nowrap'>upto</span>
            <Input
              variant='filled'
              placeholder='0'
              dimension='sm'
              value={maxValue ?? ''}
              onChange={e => {
                if (!inputVisibility[groupIndex]) {
                  setInputVisibility(prev => ({ ...prev, [groupIndex]: true }))
                }
                handleValueChange(groupIndex, e.target.value, 'max')
              }}
              type='number'
              className='min-w-8'
              startAdornment={
                isAmountMode ? <Icon IconComponent={CurrencyInr} size='xs' className='text-zinc-400' /> : undefined
              }
              endAdornment={
                <div className='flex items-center gap-0.5 '>
                  {!isAmountMode && <span className='text-xs text-zinc-400'>Qty</span>}
                  <IconButton
                    variant='ghost'
                    size='xxs'
                    IconComponent={X}
                    onClick={() => {
                      setInputVisibility(prev => ({ ...prev, [groupIndex]: false }))
                      handleValueChange(groupIndex, '', 'max')
                    }}
                    className='text-zinc-400'
                  />
                </div>
              }
              error={!!errors[errorPath]}
              style={{ width: `${(maxValue?.toString().length || 0) + 2}ch` }}
            />
          </div>
        )
      }

      return (
        <span
          className='text-text-xs-regular text-zinc-500 underline underline-offset-2 cursor-pointer'
          onClick={() => {
            const minValue = parseFloat(getInputValue(groupIndex) as string) || 0
            const nextValue = (minValue + 1).toString()
            setInputVisibility(prev => ({ ...prev, [groupIndex]: true }))
            handleValueChange(groupIndex, nextValue, 'max')
          }}
        >
          upto?
        </span>
      )
    }

    const displayGroups = items.length > 0 ? items : [[]]

    return (
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col '>
          {displayGroups.map((group, groupIndex) => {
            return (
              <div key={groupIndex}>
                <div className='rounded-xl flex flex-col '>
                  {(groupIndex > 0 || group.length > 0) && (
                    <div className='flex justify-between items-center pt-3'>
                      <div className='flex items-center gap-2'>
                        <div className='flex items-center gap-2 bg-zinc-50 rounded '>
                          <span className='text-xs text-zinc-500 whitespace-nowrap'>
                            {isAmountMode ? 'Worth min' : 'Qty min'}
                          </span>
                          <Input
                            variant='filled'
                            placeholder='0'
                            dimension='sm'
                            value={getInputValue(groupIndex)}
                            onChange={e => handleValueChange(groupIndex, e.target.value)}
                            type='number'
                            className='min-w-7'
                            startAdornment={
                              isAmountMode ? (
                                <Icon IconComponent={CurrencyInr} size='xs' className='text-zinc-400' />
                              ) : undefined
                            }
                            endAdornment={
                              !isAmountMode ? <span className='text-xs text-zinc-400 '>Qty</span> : undefined
                            }
                            error={
                              !!errors[
                                `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.${
                                  isAmountMode ? 'price' : 'quantity'
                                }`
                              ]
                            }
                            style={{ width: `${(getInputValue(groupIndex)?.toString().length || 0) + 2}ch` }}
                          />
                        </div>

                        {renderMaxValueInput(groupIndex)}
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className='text-text-xs-medium text-zinc-500'>Group {groupIndex + 1}</span>
                        {groupIndex > 0 && (
                          <IconButton
                            onClick={() => handleRemoveGroup(groupIndex)}
                            variant='ghost'
                            size='xs'
                            IconComponent={X}
                            className='text-zinc-500'
                          />
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <FormErrorText
                      message={
                        errors[
                          `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.${
                            isAmountMode ? 'price' : 'quantity'
                          }`
                        ] ||
                        errors[
                          `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.${
                            isAmountMode ? 'maxPrice' : 'maxQuantity'
                          }`
                        ]
                      }
                    />
                  </div>

                  <CatalogueGroupControl
                    groupIndex={groupIndex}
                    includedGroups={displayGroups}
                    excludedGroups={prerequisite.metaData?.excludedCatalogue}
                    catalogueType={catalogueType}
                    actions={{
                      onRemoveIncludedItem: handleRemoveItemFromGroup,
                      onRemoveExcludedItem: handleRemoveExcludedItem,
                      onUpdateItem: handleVariantUpdate,
                      onItemsSelected: handleItemsSelected,
                      onAddEmptyGroup: handleAddEmptyGroup
                    }}
                    errorHandling={{
                      errors: discountCatalogueErrors(errors, DiscountFieldPaths.PREREQUISITE_CATALOGUE)
                    }}
                    config={{
                      showVariantEditor: true,
                      addText: 'Add product',
                      showExclusionOption: true,
                      emptyText: catalogueType === 'COLLECTION' ? 'Choose Collections' : 'Choose Products'
                    }}
                  />
                </div>
                {groupIndex < items.length - 1 && (
                  <div className='h-4 flex items-center pt-1'>
                    <div className='border-b-1 border-dotted border-zinc-200 w-full ' />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='flex w-full'>
      <div className='border-l-1 border-zinc-200 w-5 rounded-l-lg border-y-1 h-auto'></div>
      <div className='flex flex-col  rounded-lg w-full px-2'>
        {renderList()}
        <FormErrorText message={errors[DiscountFieldPaths.PREREQUISITE_CATALOGUE]} />
      </div>
      <div className='border-r-1 border-zinc-200 w-5 rounded-r-lg border-y-1  h-auto'></div>
    </div>
  )
}

export default PurchaseConditionsProductList
